export type JobChainTemplateScope = 'Global' | 'Customer';

export type JobChainTemplateStatus = 'Draft' | 'Active' | 'Inactive';

export interface JobChainTemplateVersionSummary {
  id: number;
  version: number;
  status: JobChainTemplateStatus;
  createdAt: string;
  updatedAt: string | null;
}

export interface JobChainTemplateSummary {
  familyKey: string;
  scope: JobChainTemplateScope;
  scopeDisplayName: string;
  customerId: number | null;
  customerName: string | null;
  jobChainType: string;
  jobChainTypeDisplayName: string;
  jobChainTypeDescription: string | null;
  updatedAt: string | null;
  draft: JobChainTemplateVersionSummary | null;
  active: JobChainTemplateVersionSummary | null;
  inactiveVersions: JobChainTemplateVersionSummary[];
  totalUsage: number;
  runningUsage: number;
}

export interface JobChainTemplatePage {
  items: JobChainTemplateSummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface JobChainTemplateListParams {
  scope?: JobChainTemplateScope;
  customerId?: number;
  jobChainType?: string;
  status?: JobChainTemplateStatus;
  version?: number;
  page: number;
  pageSize: number;
}

export interface JobChainTemplateCustomerOption {
  id: number;
  name: string;
}

export interface JobChainTemplateTypeOption {
  value: string;
  label: string;
  description: string | null;
}

export interface JobChainTemplateCapabilities {
  canCreateGlobal: boolean;
  canCreateCustomer: boolean;
}

export interface JobChainTemplateMetadata {
  templateJobs: Array<{
    id: number;
    name: string;
    description: string | null;
    jobType: string;
    isSupported?: boolean;
  }>;
  conditionFields: JobChainTemplateConditionField[];
  authorizedScopes: JobChainTemplateScope[];
  customers: JobChainTemplateCustomerOption[];
  jobChainTypes: JobChainTemplateTypeOption[];
  capabilities: JobChainTemplateCapabilities;
}

export type JobChainTemplateAction =
  | 'CreateDraft'
  | 'SaveGraph'
  | 'Validate'
  | 'Publish'
  | 'Discard';
export type ConditionOperator =
  | 'Equals'
  | 'NotEquals'
  | 'GreaterThan'
  | 'GreaterOrEqual'
  | 'LessThan'
  | 'LessOrEqual'
  | 'Contains'
  | 'NotContains'
  | 'StartsWith'
  | 'EndsWith'
  | 'In'
  | 'NotIn'
  | 'IsNull'
  | 'IsNotNull';
export interface JobChainTemplateConditionField {
  key: string;
  valueType: string;
  nullable: boolean;
  allowedValues: string[];
  operators: ConditionOperator[];
  definitionSource: string;
}
export interface TemplateNode {
  key: string;
  jobTemplateId: number;
  joinPolicy: 'AnyLive' | 'AllLive';
  deleted: boolean | null;
  id: number | null;
}
export interface TemplateCondition {
  groupId: number;
  name: string;
  condition: ConditionOperator;
  value: string | null;
  id: number | null;
}
export interface TemplateEdge {
  key: string;
  fromKey: string;
  toKey: string;
  groupId: number | null;
  conditions: TemplateCondition[];
  id: number | null;
}
export interface JobChainTemplateGraph {
  nodes: TemplateNode[];
  edges: TemplateEdge[];
}
export interface JobChainTemplateIssue {
  code: string;
  message: string;
  nodeKey: string | null;
  edgeKey: string | null;
  conditionIndex: number | null;
  isWarning: boolean;
}
export interface JobChainTemplateAudit {
  id: number;
  actor: string;
  timeUtc: string;
  action: string;
  result: string;
  correlationId: string | null;
  data: string;
}
export interface JobChainTemplateDetail {
  id: number;
  scope: JobChainTemplateScope;
  customerId: number | null;
  jobChainType: string;
  version: number;
  status: JobChainTemplateStatus;
  etag: string;
  createdAt: string;
  updatedAt: string | null;
  graph: JobChainTemplateGraph;
  unsupportedReasons: JobChainTemplateIssue[];
  previousPublishedTemplateId: number | null;
  totalUsage: number;
  runningUsage: number;
  history: Array<{
    id: number;
    version: number;
    status: JobChainTemplateStatus;
  }>;
  audit: JobChainTemplateAudit[];
  publication: JobChainTemplateAudit | null;
  availableActions: JobChainTemplateAction[];
}
export interface CreateJobChainTemplateRequest {
  scope: JobChainTemplateScope;
  customerId?: number | null;
  jobChainType: string;
}
export interface JobChainTemplateValidation {
  etag: string;
  isValid: boolean;
  issues: JobChainTemplateIssue[];
}
export interface JobChainTemplateDraftResult {
  template: JobChainTemplateDetail;
  created: boolean;
}

export interface JobChainTemplateProblemDetails {
  type?: string | null;
  title?: string | null;
  status?: number | null;
  detail?: string | null;
  instance?: string | null;
  code?: string | null;
  correlationId?: string | null;
  [extension: string]: unknown;
}

export interface JobChainTemplateInterceptorFailure {
  errorResponse: true;
  code: number;
  message: unknown;
}

export interface JobChainTemplateNormalizedFailure {
  success: false;
  code: number;
  message: unknown;
}

export type JobChainTemplateApiFailure =
  | JobChainTemplateInterceptorFailure
  | JobChainTemplateNormalizedFailure;

export interface JobChainTemplateApiSuccess<T> {
  success: true;
  code: number;
  data: T;
}

export type JobChainTemplateApiResponse<T> =
  | JobChainTemplateApiSuccess<T>
  | JobChainTemplateApiFailure;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isOptionalNullableString = (value: unknown) =>
  value === undefined || value === null || typeof value === 'string';

export const isJobChainTemplateProblemDetails = (
  value: unknown,
): value is JobChainTemplateProblemDetails =>
  isRecord(value) &&
  isOptionalNullableString(value.type) &&
  isOptionalNullableString(value.title) &&
  (value.status === undefined ||
    value.status === null ||
    typeof value.status === 'number') &&
  isOptionalNullableString(value.detail) &&
  isOptionalNullableString(value.instance) &&
  isOptionalNullableString(value.code) &&
  isOptionalNullableString(value.correlationId);

export const isJobChainTemplateApiFailure = (
  value: unknown,
): value is JobChainTemplateApiFailure => {
  if (!isRecord(value) || !Number.isInteger(value.code)) {
    return false;
  }

  return value.errorResponse === true || value.success === false;
};
